/**
 * Заметка целиком: галерея слева, сама заметка справа. Единственное место её правки.
 *
 * ОНА ЗАМЕНИЛА СОБОЙ ТРИ ПОВЕРХНОСТИ (notes-v4): раскрытую карточку, попап на карте и
 * лайтбокс. Владелец сказал про них одно: «заметку можно в карточку в модалке развернуть и
 * смотреть все фото в большем разрешении и там же комментарий будет» — это описание ОДНОЙ
 * вещи, а не трёх, и три набора кнопок для одного предмета и были тем, из-за чего он написал
 * «сейчас пиздец как неудобно».
 *
 * РАЗДЕЛЕНИЕ КОЛОНОК И ЕСТЬ ОТВЕТ НА «ЧТО ИМЕННО ПРАВИТСЯ». Слева то, что принадлежит КАДРУ:
 * листание, перенос в другую заметку, удаление этого кадра. Справа то, что принадлежит
 * ЗАМЕТКЕ: дата, комментарий, место, удаление заметки. Ни одна кнопка не стоит в чужой
 * колонке, и человек никогда не спрашивает «это удалит фото или заметку» — он смотрит, с
 * какой стороны нажал.
 *
 * ГАЛЕРЕЯ НАСЛЕДУЕТСЯ ЦЕЛИКОМ, потому что владелец сказал «галерея с фото сделана правильно»:
 * портал в `body` (на `.k-console` висит `backdrop-filter`, а он делает элемент контекстом
 * наложения — `position: fixed` внутри консоли заперт её границами), тёплая бистра вместо
 * чёрного фона (снимок рассматривают, чтобы понять цвет стерни и почвы), Escape на узле
 * оверлея со `stopPropagation`, кольцо Tab по `FOCUSABLE`, свайп, предзагрузка соседей,
 * возврат фокуса. Все эти механизмы выстраданы и ни один не переписан.
 *
 * ОДНО ОТЛИЧИЕ ОТ ЛАЙТБОКСА ОБЯЗАТЕЛЬНО: удаление последнего кадра окно НЕ ЗАКРЫВАЕТ. Там оно
 * было про фотографию, здесь — про заметку, и заметка без фотографий жива.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "./NoteModal.css";

import * as api from "../api/client";
import type { FieldMarker, FieldPhoto } from "../api/types";
import { formatFull, todayIso } from "../lib/format";
import { canSaveNote, hasPlace } from "../lib/notes";
import { isFileDrag } from "../map/drop";
import { T } from "../strings";
import { Glyph, glyph } from "./DrawTools";
import { NotePicker } from "./NotePicker";

/** Сколько стоит вопрос об удалении, если на него не ответили. Как у карточки поля. */
const DELETE_DISARM_MS = 5000;

/** Свайп: горизонталь длиннее этого и вертикаль короче второго — иначе это прокрутка. */
const SWIPE_MIN_X = 48;
const SWIPE_MAX_Y = 32;

export interface PhotoOutcome {
  ok: boolean;
  message: string | null;
}

/**
 * Кольцо Tab: всё, на чём фокус в этом окне может остановиться.
 *
 * `:not(:disabled)` — на всех полях, а не только на кнопках: погашенная стрелка листания на
 * краю серии фокуса не берёт, и «последним» в кольце оказывался тот, на ком Tab никогда не
 * останавливается.
 */
const FOCUSABLE =
  "button:not(:disabled), input:not(:disabled), textarea:not(:disabled), " +
  'select:not(:disabled), [href], [tabindex]:not([tabindex="-1"])';

export const focusableIn = (root: HTMLElement): HTMLElement[] => [
  ...root.querySelectorAll<HTMLElement>(FOCUSABLE),
];

/** Что правит правая колонка, пока открыт редактор. Живёт над окном — переживает вкладку. */
export interface NoteDraftText {
  text: string;
  date: string;
}

interface Props {
  fieldId: number;
  /**
   * Заметка, которую показывает окно. `null` — режим СОЗДАНИЯ: заметки ещё нет, справа стоит
   * тот же редактор, слева накопленные файлы штриховыми ячейками.
   */
  note: FieldMarker | null;
  /** Лента ЭТОЙ заметки, в порядке показа. */
  photos: FieldPhoto[];
  /** Какой кадр открыт. `null` — первый (или ни одного, если лента пуста). */
  openId: number | null;
  onOpenIdChange: (photoId: number) => void;
  onClose: () => void;

  /** Открыт ли редактор правой колонки, и что в нём набрано. */
  editing: NoteDraftText | null;
  onEditing: (editing: NoteDraftText | null) => void;
  /** Сохранить слова и дату одним PATCH (или POST в режиме создания). */
  onSave: (text: string, happenedOn: string) => Promise<PhotoOutcome>;

  /** Файлы, накопленные до сохранения. Есть только в режиме создания. */
  draftFiles?: File[];
  onAddFiles: (files: File[]) => void;
  /**
   * Заменить состав накопленных файлов целиком — это крестик на ячейке. Наверх ходило только
   * «добавить», и выбравший двадцать вместо десяти мог лишь отменить всю заметку и начать
   * заново.
   */
  onDraftFiles?: (files: File[]) => void;
  /** Потолок кадров этой заметки и поля: кнопка гаснет ДО отказа. */
  photosFull?: boolean;
  fullReason?: string | null;

  onDeleteNote: () => Promise<PhotoOutcome>;
  onDeletePhoto: (photoId: number) => Promise<PhotoOutcome>;
  onMovePhoto: (photoId: number, markerId: number) => Promise<PhotoOutcome>;
  /** Взвести place-режим. Окно при этом ЗАКРЫВАЕТСЯ: тыкать надо в карту. */
  onPlace: () => void;
  onClearPlace: () => Promise<PhotoOutcome>;

  /** Куда можно перенести кадр. Одна заметка — переносить некуда, кнопки нет. */
  notes?: FieldMarker[];
  /** Фотографии ВСЕГО поля: список переноса опознаёт заметки их кадрами. */
  fieldPhotos?: FieldPhoto[];
  thumbUrl?: (photoId: number) => Promise<string>;
}

/**
 * Полные кадры, по одному на id, с отзывом на размонтировании.
 *
 * Соседи тянутся заранее: на мобильном интернете кадр приходит секунды, и листание «вперёд,
 * подождать, вперёд» превращает просмотр десяти фотографий в десять ожиданий.
 *
 * «ЖИВ ЛИ ПРОСМОТР» — РЕФОМ, А НЕ ПЕРЕМЕННОЙ ЭФФЕКТА. Переменная гаснет уборкой, а уборка
 * случается на КАЖДОМ перезапуске эффекта, то есть на всяком новом массиве `photos`.
 * Родитель перерисовывается часто (прогресс прогона, очередь загрузки, вспышки), и кадр,
 * запрошенный до перерисовки, приезжал в мёртвое замыкание и отзывался на месте: окно висело
 * на «Загрузка…» при полностью исправной сети. Дефект пойман и закрыт тестом.
 */
const useFullImages = (fieldId: number, photos: FieldPhoto[], openId: number | null) => {
  const urls = useRef(new Map<number, string>());
  const failed = useRef(new Set<number>());
  const [, bump] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    const held = urls.current;
    return () => {
      alive.current = false;
      for (const url of held.values()) URL.revokeObjectURL(url);
      held.clear();
    };
  }, []);

  useEffect(() => {
    if (openId === null) return;
    const index = photos.findIndex((photo) => photo.id === openId);
    if (index < 0) return;
    // Открытый кадр первым, соседи следом: порядок здесь и есть приоритет запросов.
    const wanted = [photos[index], photos[index - 1], photos[index + 1]].filter(
      (photo): photo is FieldPhoto => photo !== undefined,
    );
    void (async () => {
      for (const photo of wanted) {
        if (!alive.current) return;
        if (urls.current.has(photo.id) || failed.current.has(photo.id)) continue;
        try {
          const url = await api.photoObjectUrl(fieldId, photo.id, "file");
          if (!alive.current) {
            URL.revokeObjectURL(url);
            return;
          }
          urls.current.set(photo.id, url);
          // Декодирование заранее: без него сосед всё равно «моргнёт» при показе.
          if (photo.id !== openId) new Image().src = url;
          bump((token) => token + 1);
        } catch {
          if (!alive.current) return;
          failed.current.add(photo.id);
          bump((token) => token + 1);
        }
      }
    })();
  }, [fieldId, photos, openId]);

  return urls.current;
};

/** Миниатюры рельсы: те же кадры, но маленькие. Отзыв — на размонтировании, одним проходом. */
const useRailThumbs = (
  photos: FieldPhoto[],
  load: ((photoId: number) => Promise<string>) | undefined,
): Map<number, string> => {
  const urls = useRef(new Map<number, string>());
  const asked = useRef(new Set<number>());
  const [, bump] = useState(0);
  const alive = useRef(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    alive.current = true;
    const held = urls.current;
    return () => {
      alive.current = false;
      for (const url of held.values()) URL.revokeObjectURL(url);
      held.clear();
    };
  }, []);

  // Ключ по идентификаторам, а не по массиву: пересобранный список тех же кадров — тот же
  // список, и перезапускать из-за него нечего.
  const key = photos.map((photo) => photo.id).join(",");
  useEffect(() => {
    const get = loadRef.current;
    if (!get) return;
    for (const id of key === "" ? [] : key.split(",").map(Number)) {
      if (asked.current.has(id)) continue;
      asked.current.add(id);
      void get(id).then(
        (url) => {
          if (!alive.current) {
            URL.revokeObjectURL(url);
            return;
          }
          urls.current.set(id, url);
          bump((token) => token + 1);
        },
        () => {
          // Один кадр не пришёл — ячейка рельсы остаётся пустой рамкой; листание работает.
        },
      );
    }
  }, [key]);

  return urls.current;
};

/**
 * Превью ВЫБРАННЫХ, НО ЕЩЁ НЕ ОТПРАВЛЕННЫХ файлов. Их нет ни на сервере, ни в кэше — они есть
 * только в памяти этой вкладки, и показать их стоит одного вызова.
 *
 * ОТЗЫВ ОБЯЗАТЕЛЕН И ПОТОМУ СТОИТ В УБОРКЕ: каждый blob-URL держит декодированную картинку до
 * конца жизни документа, а двадцать кадров с телефона — это десятки мегабайт на вкладку.
 * Уборка срабатывает и при смене состава, и при закрытии окна (в том числе «Отменой»
 * создания), то есть на всех путях, какими эта сетка уходит с экрана.
 *
 * ЗАВИСИМОСТЬ — САМ МАССИВ, и это здесь законно (в отличие от `photos` выше): он приходит из
 * `useNotesUi.draft.files` и меняет личность ровно тогда, когда меняется состав. Пустой случай
 * разобран отдельно — родитель отдаёт свежий `[]` на каждый свой рендер, и без этой ветки
 * перерисовка прогона гоняла бы состояние впустую.
 */
const useDraftPreviews = (files: File[]): string[] => {
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    if (files.length === 0) {
      setUrls((current) => (current.length === 0 ? current : []));
      return;
    }
    const made = files.map((file) => URL.createObjectURL(file));
    setUrls(made);
    return () => {
      for (const url of made) URL.revokeObjectURL(url);
    };
  }, [files]);

  return urls;
};

export function NoteModal({
  fieldId,
  note,
  photos,
  openId,
  onOpenIdChange,
  onClose,
  editing,
  onEditing,
  onSave,
  draftFiles = [],
  onAddFiles,
  onDraftFiles,
  photosFull = false,
  fullReason = null,
  onDeleteNote,
  onDeletePhoto,
  onMovePhoto,
  onPlace,
  onClearPlace,
  notes = [],
  fieldPhotos = [],
  thumbUrl,
}: Props) {
  const overlay = useRef<HTMLDivElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const textarea = useRef<HTMLTextAreaElement | null>(null);
  const [armedPhoto, setArmedPhoto] = useState(false);
  const [armedNote, setArmedNote] = useState(false);
  const [picking, setPicking] = useState(false);
  /** Предел, названный плиткой «добавить» в ответ на нажатие. Стоит рядом с ней, а не в колонке. */
  const [limitSaid, setLimitSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Файл висит НАД зоной: без отклика на ней самой не видно, что целился верно. */
  const [over, setOver] = useState(false);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);

  /** Создание: заметки ещё нет, и половина органов бессмысленна. */
  const creating = note === null;
  const index = openId === null ? -1 : photos.findIndex((photo) => photo.id === openId);
  const photo = index < 0 ? null : (photos[index] ?? null);
  const images = useFullImages(fieldId, photos, photo?.id ?? null);
  const rail = useRailThumbs(photos, thumbUrl);
  const previews = useDraftPreviews(draftFiles);
  const src = photo === null ? undefined : images.get(photo.id);
  const date = note === null ? todayIso() : note.happened_on;
  const dateText = formatFull(date);

  const step = useCallback(
    (delta: number): void => {
      const next = photos[index + delta];
      if (next === undefined) return;
      // Взведённый вопрос принадлежит КАДРУ, а не окну: уехать с ним на соседний значит
      // предложить удалить не то, о чём спрашивали.
      setArmedPhoto(false);
      setError(null);
      onOpenIdChange(next.id);
    },
    [index, onOpenIdChange, photos],
  );

  /**
   * ФОКУС — НА КОНТЕЙНЕР ПРИ ОТКРЫТИИ И ОБРАТНО ПРИ ЗАКРЫТИИ.
   *
   * Без первого первый Tab уводит в список полей за подложкой: узлы под ней из фокусного
   * порядка не исчезают. Без второго фокус остаётся на `<body>` — для клавиатуры это начало
   * страницы, то есть выход из консоли.
   */
  const noteId = note?.id ?? null;
  useEffect(() => {
    const opener = document.activeElement;
    overlay.current?.focus();
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
        return;
      }
      /**
       * КАРТОЧКИ, ОТДАВШЕЙ ФОКУС, МОГЛО НЕ СТАТЬ — её удалили из этого же окна. Тогда
       * отступаем на первую живую карточку, а если список опустел — на «Новую заметку»:
       * фокус на `<body>` для клавиатуры это начало страницы, то есть выход из консоли.
       */
      const own =
        noteId === null
          ? null
          : document.querySelector(`[data-note-id="${noteId}"] .k-notecard__open`);
      const fallback =
        own ??
        document.querySelector(".k-notecard__open") ??
        document.querySelector('[data-testid="notes-add"]');
      if (fallback instanceof HTMLElement) fallback.focus();
    };
    // Снимок делается ОДИН РАЗ, при открытии: заметка под окном может смениться, а отдавал
    // фокус тот, кто был в начале.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ФОКУС НЕ ИМЕЕТ ПРАВА УПАСТЬ НА `body`. Кнопка, исчезающая в момент нажатия («Удалить»,
   * «Перенести», стрелка на краю серии), уносит с собой фокус, а Escape ловится на узле окна —
   * то есть Escape умирал бы ровно после действия, которым его чаще всего и отменяют.
   */
  useEffect(() => {
    if (editing !== null) {
      textarea.current?.focus();
      return;
    }
    if (document.activeElement === document.body) overlay.current?.focus();
  }, [armedPhoto, armedNote, picking, editing, openId]);

  /**
   * КРЕСТИК УНОСИТ С СОБОЙ СВОЙ ФОКУС, и упасть ему на `body` нельзя — для клавиатуры это
   * начало страницы, то есть выход из окна, в котором только что работали. Преемник назван
   * позицией: крестик следующей ячейки, а если убрали последнюю — плитка «＋», а если убрали
   * вообще всё — большая зона, которая встала на место сетки.
   */
  const wantFocus = useRef<number | null>(null);
  useEffect(() => {
    const want = wantFocus.current;
    if (want === null) return;
    wantFocus.current = null;
    const root = overlay.current;
    if (root === null) return;
    const crosses = [...root.querySelectorAll<HTMLElement>('[data-testid="modal-draft-remove"]')];
    // По классу, а не по testid: точка прикрепления одна, но обликов у неё два, и оба носят
    // одно имя для теста — искать надо ту, что сейчас на экране.
    const fallback =
      root.querySelector<HTMLElement>(".k-modal__draftadd") ??
      root.querySelector<HTMLElement>(".k-modal__drop");
    (crosses[want] ?? fallback)?.focus();
  }, [draftFiles.length]);

  /**
   * ОТКАЗ «НАПИШИТЕ ИЛИ ДОБАВЬТЕ ФОТО» СНИМАЕТСЯ ПРИ ЛЮБОМ СОДЕРЖИМОМ.
   *
   * Он оставался стоять, пока прикрепляли файлы, и экран говорил две вещи разом: «добавьте
   * фото» и «8 фото добавятся после сохранения». Требование выполнено ровно в тот момент,
   * когда появилось содержимое, — и отказ обязан уйти тогда же, а не после второго нажатия.
   */
  const filled = canSaveNote(editing?.text ?? "", draftFiles.length);
  useEffect(() => {
    if (filled) setError((current) => (current === T.markers.needText ? null : current));
  }, [filled]);

  /** Вопросы, на которые не ответили, снимаются сами. */
  useEffect(() => {
    if (!armedPhoto && !armedNote) return;
    const timer = window.setTimeout(() => {
      setArmedPhoto(false);
      setArmedNote(false);
    }, DELETE_DISARM_MS);
    return () => window.clearTimeout(timer);
  }, [armedPhoto, armedNote]);

  /**
   * КАДР УШЁЛ ИЗ ЛЕНТЫ, А ЛЕНТА ЖИВА — окно остаётся на соседнем. Так бывает от переноса и от
   * удаления. Позиция берётся с прошлого рендера, потому что своей у исчезнувшего уже нет.
   */
  const lastIndex = useRef(0);
  useEffect(() => {
    if (photos.length === 0) return;
    if (index >= 0) {
      lastIndex.current = index;
      return;
    }
    const next = photos[lastIndex.current] ?? photos[photos.length - 1];
    if (next !== undefined) onOpenIdChange(next.id);
  }, [index, photos, onOpenIdChange]);

  /**
   * ОТМЕНА В СОЗДАНИИ — ЭТО ЗАКРЫТИЕ ОКНА.
   *
   * У правки существующей заметки есть куда возвращаться — в чтение. У создания некуда:
   * заметки нет, и «Отмена», закрывшая только редактор, оставляла на экране призрак —
   * сегодняшнюю дату, «без описания» и кнопку «Удалить» у того, чего в базе не существует.
   */
  const cancelEditing = (): void => {
    setError(null);
    if (creating) {
      onClose();
      return;
    }
    onEditing(null);
  };

  const startEditing = (): void => {
    setError(null);
    onEditing({ text: note?.note ?? "", date });
  };

  const save = async (): Promise<void> => {
    if (editing === null) return;
    // СОЗДАТЬ ПУСТУЮ НЕЛЬЗЯ, СТАТЬ ПУСТОЙ МОЖНО — и проверка про СОДЕРЖИМОЕ, а не про текст:
    // восемь кадров это уже содержимое.
    if (creating && !canSaveNote(editing.text, draftFiles.length)) {
      setError(T.markers.needText);
      return;
    }
    setBusy(true);
    setError(null);
    const result = await onSave(editing.text.trim(), editing.date);
    setBusy(false);
    if (!result.ok) {
      setError(result.message ?? T.common.unknownError);
      return;
    }
    onEditing(null);
  };

  const removePhoto = async (): Promise<void> => {
    if (busy || photo === null) return;
    setBusy(true);
    setError(null);
    const successor = photos[index + 1] ?? photos[index - 1] ?? null;
    const result = await onDeletePhoto(photo.id);
    setBusy(false);
    if (!result.ok) {
      setError(result.message ?? T.common.unknownError);
      return;
    }
    setArmedPhoto(false);
    // Кадров не осталось вовсе → окно НЕ закрывается: оно про заметку, а заметка жива.
    if (successor !== null) onOpenIdChange(successor.id);
  };

  const removeNote = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const result = await onDeleteNote();
    setBusy(false);
    if (result.ok) return; // окно уходит вместе с заметкой
    setError(result.message ?? T.common.unknownError);
  };

  const move = async (markerId: number): Promise<void> => {
    if (busy || photo === null || markerId === photo.marker_id) return;
    setBusy(true);
    setError(null);
    const result = await onMovePhoto(photo.id, markerId);
    setBusy(false);
    if (result.ok) {
      setPicking(false);
      return;
    }
    if (result.message !== null) setError(result.message);
  };

  const take = (files: FileList | null): void => {
    const chosen = [...(files ?? [])];
    if (chosen.length > 0) onAddFiles(chosen);
  };

  /** Tab замкнут внутри оверлея: счёт узлов не зашит — он меняется с состоянием колонок. */
  const trapTab = (event: React.KeyboardEvent): void => {
    if (event.key !== "Tab" || overlay.current === null) return;
    const focusable = focusableIn(overlay.current);
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === overlay.current)) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /**
   * ESCAPE ОТМЕНЯЕТ БЛИЖАЙШЕЕ, ПО ОДНОМУ НАЖАТИЮ НА СЛОЙ, и гасится на узле: на `window`
   * висит выход из place-режима, и закрыть окно, заодно выключив режим, — это два действия на
   * одно нажатие.
   */
  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.stopPropagation();
      event.preventDefault();
      if (picking) {
        setPicking(false);
        return;
      }
      if (armedPhoto || armedNote) {
        setArmedPhoto(false);
        setArmedNote(false);
        return;
      }
      if (editing !== null) {
        cancelEditing();
        return;
      }
      onClose();
      return;
    }
    if (event.key === "Tab") {
      trapTab(event);
      return;
    }
    // Стрелки листают, пока не набирают текст: в поле даты ←/→ двигают каретку.
    if (editing !== null) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      step(-1);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      step(1);
    }
  };

  const attachable = useMemo(() => notes.filter((item) => item.id !== undefined), [notes]);
  const placed = note !== null && hasPlace(note);
  const text = note?.note.trim() ?? "";

  // --- левая колонка: кадр, рельса, действия кадра -------------------------
  const gallery = photos.length > 0 && (
    /* СЧЁТЧИК ЦИФРАМИ — ДЛЯ ГЛАЗА, СЛОВАМИ — ДЛЯ СКРИНРИДЕРА. Моноширинный ряд «3 / 8»
       читается с одного взгляда и русского в себе не имеет, но вслух он звучит как «три
       дробь восемь»; поэтому сама колонка названа словами, а цифры скрыты от озвучки. */
    <div
      className="k-modal__photo"
      role="group"
      aria-label={T.photos.position(index + 1, photos.length)}
    >
      <div className="k-modal__stage" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="k-iconbtn k-modal__step k-modal__step--prev"
          aria-label={T.photos.prev}
          // Края НЕ зациклены: погасшая кнопка говорит, что это край, а прыжок в конец
          // говорил бы, что края нет.
          disabled={index <= 0}
          onClick={() => step(-1)}
        >
          <span aria-hidden="true">‹</span>
        </button>

        {/* `key` по id: смена `src` на одном узле оставляла прежний кадр видимым, пока новый
            декодируется, — на медленной связи это «следующее фото» с картинкой предыдущего. */}
        {src === undefined ? (
          <p className="k-caption k-modal__loading">{T.common.loading}</p>
        ) : (
          photo !== null && (
            <img
              key={photo.id}
              className="k-modal__frame"
              src={src}
              width={photo.width}
              height={photo.height}
              alt={
                text === "" ? formatFull(photo.taken_on) : `${formatFull(photo.taken_on)}: ${text}`
              }
              decoding="async"
            />
          )
        )}

        <button
          type="button"
          className="k-iconbtn k-modal__step k-modal__step--next"
          aria-label={T.photos.next}
          disabled={index >= photos.length - 1}
          onClick={() => step(1)}
        >
          <span aria-hidden="true">›</span>
        </button>

        {/* СЧЁТЧИК ПРИНАДЛЕЖИТ КАДРУ, И МЕСТО ЕМУ НА КАДРЕ. В строке рельсы «1 / 8» висело
            сиротой у правого края, ни к чему не привязанное; в углу колодца оно читается как
            подпись к тому, на что смотрят. */}
        <span className="k-mono k-modal__count" aria-hidden="true">
          {index + 1} / {photos.length}
        </span>
      </div>

      <div className="k-modal__railrow">
        {/* РЕЛЬСА отвечает на «сколько ещё и что там», не листая по одной. Это не `tablist`:
            кадры не вкладки, и роль переключателя обещала бы смену панелей. */}
        <div
          className="k-modal__rail"
          role="group"
          aria-label={T.markers.photoRail}
          data-testid="modal-rail"
        >
          {/* ПЛИТКА «ДОБАВИТЬ» ПЕРВОЙ В РЕЛЬСЕ (слова владельца: «плейсхолдер какой-то или
              кнопка в модалке там где фото, можно в начале списка нижнего»). Штриховая рамка
              — тот же язык, что у ячеек накопленных файлов: место, где кадра ещё нет.

              В ЛИСТАНИИ И СЧЁТЧИКЕ ОНА НЕ УЧАСТВУЕТ: «1 / 9» на восьми кадрах сказало бы, что
              их девять. Считается по `photos`, а плитка в нём не лежит. */}
          <button
            type="button"
            className="k-modal__railadd"
            data-testid="modal-rail-add"
            aria-label={T.photos.add}
            onClick={() => {
              // ПРЕДЕЛ В МОМЕНТ НАМЕРЕНИЯ: плитка не гаснет и не прячется — погашенный орган
              // без объяснения не говорит, ни что кончилось, ни где освобождать.
              if (photosFull) {
                setLimitSaid(fullReason);
                return;
              }
              setLimitSaid(null);
              input.current?.click();
            }}
          >
            <Glyph d={glyph.plus} size={18} stroke={2} />
          </button>
          {photos.map((item) => {
            const url = rail.get(item.id);
            return (
              <button
                key={item.id}
                type="button"
                className={`k-modal__railcell${item.id === photo?.id ? " k-modal__railcell--on" : ""}`}
                data-testid="modal-rail-cell"
                data-photo-id={item.id}
                aria-label={formatFull(item.taken_on)}
                aria-current={item.id === photo?.id}
                onClick={() => onOpenIdChange(item.id)}
              >
                {url !== undefined && <img src={url} alt="" decoding="async" />}
              </button>
            );
          })}
        </div>
      </div>

      {limitSaid !== null && (
        <p className="k-hint" role="alert" data-testid="modal-rail-limit">
          {limitSaid}
        </p>
      )}

      {armedPhoto ? (
        <div className="k-note k-note--error" role="alert">
          <p>{T.photos.deleteAsk(photo === null ? dateText : formatFull(photo.taken_on))}</p>
          <div className="k-row k-row--wrap">
            <button
              type="button"
              className="k-btn k-btn--danger k-btn--compact"
              disabled={busy}
              onClick={() => void removePhoto()}
            >
              {T.tools.deleteYes}
            </button>
            <button
              type="button"
              className="k-btn k-btn--ghost k-btn--compact"
              onClick={() => setArmedPhoto(false)}
            >
              {T.tools.deleteNo}
            </button>
          </div>
        </div>
      ) : picking ? (
        <div className="k-modal__picker">
          <span className="k-label">{T.photos.attachLabel}</span>
          <NotePicker
            notes={attachable}
            photos={fieldPhotos}
            currentId={photo?.marker_id ?? 0}
            busy={busy}
            thumbUrl={thumbUrl}
            onPick={(id) => void move(id)}
          />
          <button
            type="button"
            className="k-btn k-btn--ghost k-btn--compact"
            onClick={() => setPicking(false)}
          >
            {T.fieldCard.cancel}
          </button>
        </div>
      ) : (
        <div className="k-row k-row--wrap">
          {attachable.length > 1 && (
            <button
              type="button"
              className="k-btn k-btn--compact"
              data-testid="modal-move-photo"
              onClick={() => setPicking(true)}
            >
              {T.photos.attachLabel}
            </button>
          )}
          <button
            type="button"
            className="k-btn k-btn--danger k-btn--compact"
            data-testid="modal-delete-photo"
            onClick={() => setArmedPhoto(true)}
          >
            {T.photos.delete}
          </button>
        </div>
      )}
    </div>
  );

  const dragOver = (event: React.DragEvent): void => {
    if (!isFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setOver(true);
  };

  const dragLeave = (event: React.DragEvent): void => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setOver(false);
  };

  const dropFiles = (event: React.DragEvent): void => {
    if (!isFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    setOver(false);
    take(event.dataTransfer.files);
  };

  /**
   * ТОЧКА ПРИКРЕПЛЕНИЯ ВСЕГДА ОДНА И ВСЕГДА ПЕРВАЯ В РЯДУ КАДРОВ. Ряда нет — его место целиком
   * занимает большая зона; ряд есть — точка стоит первой плиткой в нём.
   *
   * ЭТОГО ОРГАНА НЕ БЫЛО В СОЗДАНИИ ВОВСЕ, и владелец назвал это первым. Плитка «＋» живёт в
   * рельсе, а рельса — внутри галереи, закрытой гейтом `photos.length > 0`; кнопка «Добавить
   * фото» жила в ветке чтения, а при создании рисуется только редактор. Обе точки отсутствовали
   * одновременно, и прикрепить фотографию к новой заметке было физически нечем.
   *
   * ЗОНА И РЕЛЬСА ВЗАИМНО ИСКЛЮЧЕНЫ ПО ПОСТРОЕНИЮ, лишнего условия не нужно: рельса живёт при
   * `photos.length > 0`, зона — при `photos.length === 0`. Двух точек на экране не бывает.
   *
   * СТОИТ МЕЖДУ ТЕКСТОМ И «СОХРАНИТЬ», а не после кнопок: человек, дописавший фразу и
   * поведший взгляд к кнопке, проходит через зону и не может её не увидеть.
   *
   * ПОСЛЕ ВЫБОРА ОНА СХЛОПЫВАЕТСЯ В ПЛИТКУ ВО ГЛАВЕ СЕТКИ: 7rem пустоты отнимали бы место у
   * того, ради чего их выбрали, а действие остаётся тем же и потому выглядит той же штриховой
   * рамкой — только размером с ячейку.
   */
  const attachZone = (
    <div className="k-modal__zone" onDragOver={dragOver} onDragLeave={dragLeave} onDrop={dropFiles}>
      {draftFiles.length === 0 ? (
        <button
          type="button"
          className="k-modal__drop"
          data-testid="modal-add-photo"
          data-over={over || undefined}
          onClick={() => {
            // НА ПОТОЛКЕ ЗОНА НЕ ГАСНЕТ, а называет предел: погашенный орган без объяснения не
            // говорит, ни что кончилось, ни где освобождать. То же поведение, что у плитки.
            if (photosFull) {
              setLimitSaid(fullReason);
              return;
            }
            setLimitSaid(null);
            input.current?.click();
          }}
        >
          <span className="k-modal__dropmark" aria-hidden="true">
            <Glyph d={glyph.plus} size={28} stroke={2} />
          </span>
          <span className="k-modal__dropline">{T.photos.add}</span>
          {/* Строка своя, а не панельная: `markers.dropHint` называет ИСХОД раскладки по
              местам съёмки, а здесь исход другой и единственный — все кадры ложатся в эту
              самую заметку. */}
          <span className="k-modal__drophint">{T.markers.dropToNote}</span>
        </button>
      ) : (
        <div
          className="k-modal__draft"
          data-testid="modal-draft-cells"
          data-over={over || undefined}
        >
          {/* ПОКАЗЫВАЮТСЯ ВСЕ, А НЕ ПЕРВЫЕ ЧЕТЫРЕ: сетка рисовала четыре пустых квадрата на
              восемь файлов, то есть экран противоречил строке под собой. Их только что
              выбрали и на них смотрят — пять рядов по четыре здесь нормальны. */}
          <div className="k-modal__draftgrid">
            <button
              type="button"
              className="k-modal__draftadd"
              data-testid="modal-add-photo"
              aria-label={T.photos.add}
              onClick={() => input.current?.click()}
            >
              <Glyph d={glyph.plus} size={18} stroke={2} />
            </button>
            {draftFiles.map((file, position) => (
              <span className="k-modal__draftcell" key={`${file.name}-${position}`}>
                {previews[position] !== undefined && (
                  <img src={previews[position]} alt="" decoding="async" />
                )}
                {/* КРЕСТИК ВИДЕН ВСЕГДА, А НЕ ПО НАВЕДЕНИЮ: на телефоне наведения нет, а пачку
                    с поля разбирают чаще всего именно с телефона. */}
                <button
                  type="button"
                  className="k-modal__draftdrop"
                  data-testid="modal-draft-remove"
                  aria-label={T.markers.draftRemove}
                  onClick={() => {
                    wantFocus.current = position;
                    onDraftFiles?.(draftFiles.filter((_, other) => other !== position));
                  }}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </span>
            ))}
          </div>
          <p className="k-hint">{T.markers.draftPhotos(draftFiles.length)}</p>
        </div>
      )}
      {limitSaid !== null && (
        <p className="k-hint" role="alert" data-testid="modal-drop-limit">
          {limitSaid}
        </p>
      )}
    </div>
  );

  // --- правая колонка: сама заметка ----------------------------------------
  const editor = editing !== null && (
    <div className="k-modal__editor">
      <label className="k-field">
        <span className="k-label">{T.markers.dateLabel}</span>
        <input
          className="k-input"
          type="date"
          value={editing.date}
          // Побывать на поле завтра нельзя.
          max={todayIso()}
          data-testid="modal-date"
          onChange={(event) => onEditing({ ...editing, date: event.target.value })}
        />
        <span className="k-hint">{T.markers.dateHint}</span>
      </label>
      <textarea
        ref={textarea}
        className="k-textarea"
        rows={6}
        maxLength={500}
        value={editing.text}
        placeholder={T.markers.placeholder}
        aria-label={T.markers.placeholder}
        data-testid="modal-text"
        onChange={(event) => onEditing({ ...editing, text: event.target.value })}
      />
      {creating && attachZone}
      {error !== null && (
        <p className="k-note k-note--error" role="alert" data-testid="modal-error">
          {error}
        </p>
      )}
      {/* ВО ВСЮ ШИРИНУ КОЛОНКИ, а не компактные: `k-btn--compact` написан для тесных рядов
          карточки, и в колонке 320 px пара мелких кнопок с полем пустоты справа выглядит
          остатком вёрстки, а не решением. */}
      <div className="k-modal__acts">
        <button
          type="button"
          className="k-btn"
          disabled={busy || editing.date === ""}
          data-testid="modal-save"
          onClick={() => void save()}
        >
          {T.fieldCard.save}
        </button>
        <button
          type="button"
          className="k-btn k-btn--ghost"
          data-testid="modal-cancel"
          onClick={cancelEditing}
        >
          {T.fieldCard.cancel}
        </button>
      </div>
    </div>
  );

  const reading = (
    <>
      <div className="k-modal__head">
        <span className="k-mono k-modal__date">{dateText}</span>
        {!creating && (
          <button
            type="button"
            className="k-notecard__chip k-notecard__chip--btn"
            data-testid="modal-place"
            onClick={onPlace}
          >
            <Glyph d={glyph.flag} size={16} stroke={1.5} />
            {placed ? T.markers.placeAgain : T.markers.noPlace}
          </button>
        )}
      </div>

      {text !== "" ? (
        // Текст ЦЕЛИКОМ: клэмп остался карточке, а окно за тем и открывают, чтобы дочитать.
        <p className="k-modal__text">{text}</p>
      ) : (
        <p className="k-modal__text k-modal__text--none">{T.markers.noText}</p>
      )}

      {error !== null && (
        <p className="k-note k-note--error" role="alert" data-testid="modal-error">
          {error}
        </p>
      )}

      {/* ТОЧКА ПРИКРЕПЛЕНИЯ ЗДЕСЬ БОЛЬШЕ НЕ СТОИТ. Кнопка «Добавить фото» жила рядом с
          «Изменить», а при ≥1 фотографии точек было уже ДВЕ: она и плитка «＋» в рельсе. Точка
          прикрепления всегда одна и всегда первая в ряду кадров — плитка среди кадров, куда
          файл и добавляют, или большая зона, если ряда кадров нет вовсе. Предел плитка
          объясняет сама, поэтому кнопка не унесла с собой ни одного ответа. */}
      <div className="k-modal__acts">
        <button type="button" className="k-btn" data-testid="modal-edit" onClick={startEditing}>
          {T.markers.edit}
        </button>
      </div>

      {/* Заметка без единого кадра — тот же случай «ряда кадров нет»: строка говорит, что есть
          сейчас, зона под ней — что делать. */}
      {photos.length === 0 && (
        <>
          <p className="k-hint">{T.markers.noPhotos}</p>
          {attachZone}
        </>
      )}

      {armedNote ? (
        <div className="k-note k-note--error" role="alert" data-testid="modal-delete-ask">
          <p>{T.markers.deleteAsk(photos.length)}</p>
          <div className="k-row k-row--wrap">
            <button
              type="button"
              className="k-btn k-btn--danger k-btn--compact"
              disabled={busy}
              onClick={() => void removeNote()}
            >
              {T.tools.deleteYes}
            </button>
            <button
              type="button"
              className="k-btn k-btn--ghost k-btn--compact"
              onClick={() => setArmedNote(false)}
            >
              {T.tools.deleteNo}
            </button>
          </div>
        </div>
      ) : (
        // НИЖНИЙ ЯРУС ПРИЖАТ К НИЗУ КОЛОНКИ (`margin-top: auto`), а линейка старшинства стала
        // его собственной верхней границей: отдельный `<hr>` рисовал ту же линию узлом.
        <div className="k-modal__danger">
          {placed && (
            <button
              type="button"
              className="k-btn k-btn--ghost k-btn--compact"
              data-testid="modal-clear-place"
              onClick={() => void onClearPlace()}
            >
              {T.markers.placeClear}
            </button>
          )}
          <button
            type="button"
            className="k-btn k-btn--danger k-btn--compact"
            data-testid="modal-delete-note"
            onClick={() => setArmedNote(true)}
          >
            {T.markers.delete}
          </button>
        </div>
      )}
    </>
  );

  return createPortal(
    <div
      className={`k-modal${photos.length === 0 ? " k-modal--narrow" : ""}`}
      ref={overlay}
      role="dialog"
      aria-modal="true"
      aria-label={creating ? T.markers.addNote : T.markers.modalLabel(dateText)}
      tabIndex={-1}
      data-testid="note-modal"
      onKeyDown={onKeyDown}
      // Клик по подложке закрывает; по колонкам — нет. Проверка по `currentTarget`, а не по
      // классу: любой потомок, включая будущий, остаётся «внутри» без правки условия.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onPointerDown={(event) => {
        swipeStart.current = { x: event.clientX, y: event.clientY };
      }}
      onPointerUp={(event) => {
        const start = swipeStart.current;
        swipeStart.current = null;
        if (start === null || editing !== null) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        // Вертикаль ограничена: иначе прокрутка пальцем листала бы кадры, а скролл рельсы
        // превращался бы в листание.
        if (Math.abs(dx) < SWIPE_MIN_X || Math.abs(dy) > SWIPE_MAX_Y) return;
        step(dx < 0 ? 1 : -1);
      }}
    >
      <div className="k-modal__box" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="k-iconbtn k-modal__close"
          aria-label={T.markers.close}
          data-testid="modal-close"
          onClick={onClose}
        >
          <span aria-hidden="true">×</span>
        </button>

        {/* Не `<label>`, хотя так короче: обёртка открывает системный выбор и тогда, когда
            кнопка внутри неё погашена, — то есть ровно на пределе, где отказ должен быть
            честным. */}
        <input
          ref={input}
          className="k-modal__file"
          type="file"
          accept="image/*"
          multiple
          tabIndex={-1}
          data-testid="modal-input"
          onChange={(event) => {
            take(event.target.files);
            event.target.value = "";
          }}
        />

        {gallery}

        <div className="k-modal__note">
          {/* ВИДИМОЕ ИМЯ ОКНА СОЗДАНИЯ. Слово «Новая заметка» жило только в `aria-label`:
              зрячий человек открывал окно, в котором не написано, что это. Ключ тот же, что
              у доступного имени, — второго названия одной вещи не бывает. У чтения и правки
              заголовка нет: там заглавие несут дата и текст самой заметки. */}
          {creating && <h2 className="k-modal__title">{T.markers.addNote}</h2>}
          {editing !== null ? editor : reading}
        </div>
      </div>
    </div>,
    document.body,
  );
}
